import * as React from 'react';
import {Query, Mutation} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {EmailAutomationType} from 'nutshell-graphql-types';
import type {
    GetEmailAutomation as GetEmailAutomationQuery,
    GetEmailAutomationVariables as GetEmailAutomationQueryVariables,
    CreateEmailAutomation as CreateEmailAutomationMutation,
    CreateEmailAutomationVariables as CreateEmailAutomationMutationVariables,
    UpdateEmailAutomation as UpdateEmailAutomationMutation,
    UpdateEmailAutomationVariables as UpdateEmailAutomationMutationVariables,
    DeleteEmailAutomation as DeleteEmailAutomationMutation,
    DeleteEmailAutomationVariables as DeleteEmailAutomationMutationVariables,
} from 'nutshell-graphql-types';

import {refetchSalesAutomationOpenLeadQueries} from '../../app/sales-automation-open-leads';
import {MAILCHIMP_EMAILS, CONSTANT_CONTACT_EMAILS, NUTSHELL_EMAIL_MARKETING} from '../../constants';
import CREATE_EMAIL_AUTOMATION from '../../graphql/mutations/create-email-automation.graphql';
import DELETE_EMAIL_AUTOMATION from '../../graphql/mutations/delete-email-automation.graphql';
import UPDATE_EMAIL_AUTOMATION from '../../graphql/mutations/update-email-automation.graphql';
import GET_EMAIL_AUTOMATION from '../../graphql/queries/get-email-automation.graphql';
import type {EmailType} from '../../types';

import {FormConstantContactComponent} from './form-constant-contact-component';
import {FormMailchimpComponent} from './form-mailchimp-component';
import {FormNutshellEmailSequenceComponent} from './form-nutshell-email-sequence-component';

type OwnProps = {
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => void;
    stageId: string;
    entityId: string | null | undefined;
    type: EmailType;
};

type RoutedProps = ContextRouter & OwnProps;

class SubscriptionQuery extends Query<GetEmailAutomationQuery, GetEmailAutomationQueryVariables> {}

class CreateMutation extends Mutation<
    CreateEmailAutomationMutation,
    CreateEmailAutomationMutationVariables
> {}

class UpdateMutation extends Mutation<
    UpdateEmailAutomationMutation,
    UpdateEmailAutomationMutationVariables
> {}

class DeleteMutation extends Mutation<
    DeleteEmailAutomationMutation,
    DeleteEmailAutomationMutationVariables
> {}

const FormEmailsGraphQL: React.FC<RoutedProps> = (props: RoutedProps) => {
    // Need a stagesetId, or else we can't proceed
    const {stagesetId} = props.match.params;
    if (!stagesetId) return null;

    const Component = getEmailListEditorFormComponent(props.type);

    return (
        <SubscriptionQuery
            query={GET_EMAIL_AUTOMATION}
            variables={{
                emailAutomationId:
                    props.entityId ||
                    '' /* It'll actually get skipped, but flow doesn't know that, so it needs a string */,
            }}
            skip={!props.entityId}
            // @ts-expect-error
            fetchPolicy='cache-and-network'
        >
            {({data, loading}) => (
                <CreateMutation
                    mutation={CREATE_EMAIL_AUTOMATION}
                    // @ts-expect-error
                    refetchQueries={refetchSalesAutomationOpenLeadQueries({stageId: props.stageId})}
                >
                    {(createEmailAutomation) => (
                        <UpdateMutation
                            mutation={UPDATE_EMAIL_AUTOMATION}
                            // @ts-expect-error
                            refetchQueries={refetchSalesAutomationOpenLeadQueries({
                                stageId: props.stageId,
                            })}
                        >
                            {(updateEmailAutomation) => (
                                <DeleteMutation
                                    mutation={DELETE_EMAIL_AUTOMATION}
                                    // @ts-expect-error
                                    refetchQueries={refetchSalesAutomationOpenLeadQueries({
                                        stageId: props.stageId,
                                    })}
                                >
                                    {(deleteEmailAutomation) => (
                                        <Component
                                            stagesetId={stagesetId}
                                            isFetchingLatestVersion={loading}
                                            stageId={props.stageId}
                                            formSubmitBtnRef={props.formSubmitBtnRef}
                                            emailAutomation={data ? data.emailAutomation : null}
                                            // @ts-expect-error
                                            createEmailAutomation={(createEmailAutomationPayload) =>
                                                createEmailAutomation({
                                                    variables: {
                                                        emailAutomation: {
                                                            ...createEmailAutomationPayload,
                                                            type: getEmailAutomationType(
                                                                props.type
                                                            ),
                                                        },
                                                    },
                                                })
                                            }
                                            // @ts-expect-error
                                            updateEmailAutomation={(updateEmailAutomationPayload) =>
                                                updateEmailAutomation({
                                                    variables: {
                                                        emailAutomation:
                                                            updateEmailAutomationPayload,
                                                    },
                                                })
                                            }
                                            // @ts-expect-error
                                            deleteEmailAutomation={(deleteEmailAutomationPayload) =>
                                                deleteEmailAutomation({
                                                    variables: {
                                                        emailAutomation:
                                                            deleteEmailAutomationPayload,
                                                    },
                                                })
                                            }
                                        />
                                    )}
                                </DeleteMutation>
                            )}
                        </UpdateMutation>
                    )}
                </CreateMutation>
            )}
        </SubscriptionQuery>
    );
};

// @ts-expect-error
export const FormEmails = withRouter<OwnProps>(FormEmailsGraphQL);

const getEmailAutomationType = (type: string): EmailAutomationType => {
    if (type === CONSTANT_CONTACT_EMAILS) {
        return 'CONSTANT_CONTACT';
    }

    if (type === MAILCHIMP_EMAILS) {
        return 'MAILCHIMP';
    }

    if (type === NUTSHELL_EMAIL_MARKETING) {
        return 'NUTSHELL_EMAIL_SEQUENCE_TEMPLATE';
    }

    // Default to the mailchimp type
    return 'MAILCHIMP';
};

const getEmailListEditorFormComponent = (type: string): React.ComponentType<any> => {
    if (type === CONSTANT_CONTACT_EMAILS) {
        return FormConstantContactComponent;
    }

    if (type === MAILCHIMP_EMAILS) {
        return FormMailchimpComponent;
    }

    if (type === NUTSHELL_EMAIL_MARKETING) {
        return FormNutshellEmailSequenceComponent;
    }

    // Default to the mailchimp type
    return FormMailchimpComponent;
};
